import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconArrowDownStepCourses,
  iconCheckSmallStep3Courses,
  iconCheckWhiteModalCourses,
  iconLightBulbStepCourses,
} from '../../assets';
import { useTheme } from '../../theme';
import Button from '../Button';
import { MultilingualField } from '../MultilingualField';
import type {
  CreateQuestionFormData,
  CreateQuestionFormValidationErrors,
  QuestionOptionText,
} from './types';
import {
  CREATE_QUESTION_MAX_OPTIONS,
  CREATE_QUESTION_MIN_OPTIONS,
  validateCreateQuestionForm,
} from './types';

export interface CreateQuestionFormProps {
  /** Dados atuais do formulário (controlado) */
  data: CreateQuestionFormData;
  /** Callback quando qualquer campo muda */
  onChange: (data: CreateQuestionFormData) => void;
  /** Opções para Difficulty Level (value, label) */
  difficultyOptions: { value: string; label: string }[];
  /** Opções para Category */
  categoryOptions: { value: string; label: string }[];
  /** Opções para Linked Course(s) (opcional) */
  courseOptions?: { value: string; label: string }[];
  /** Chamado ao clicar Cancel */
  onCancel: () => void;
  /** Chamado ao clicar Save com dados válidos */
  onSave: (data: CreateQuestionFormData) => void;
  /** Erros de validação externos (ex.: retorno da API) */
  validationErrors?: CreateQuestionFormValidationErrors;
  /** Desabilita botão Save durante envio */
  saving?: boolean;
}

const defaultOptionText = (): QuestionOptionText => ({ en: '', es: '' });

function LinkIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function ensureOptionsLength(
  options: QuestionOptionText[],
  length: number
): QuestionOptionText[] {
  const next = [...options];
  while (next.length < length) {
    next.push(defaultOptionText());
  }
  return next.slice(0, length);
}

export function CreateQuestionForm({
  data,
  onChange,
  difficultyOptions,
  categoryOptions,
  courseOptions = [],
  onCancel,
  onSave,
  validationErrors: externalErrors,
  saving = false,
}: CreateQuestionFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const validation = useMemo(() => validateCreateQuestionForm(data), [data]);
  const errors = useMemo(
    () => ({ ...validation.errors, ...externalErrors }),
    [validation.errors, externalErrors]
  );

  const handleSubmit = useCallback(() => {
    const result = validateCreateQuestionForm(data);
    if (!result.valid) return;
    onSave(data);
  }, [data, onSave]);

  const update = useCallback(
    (next: Partial<CreateQuestionFormData>) => {
      onChange({ ...data, ...next });
    },
    [data, onChange]
  );

  const addOption = useCallback(() => {
    const options = ensureOptionsLength(data.options, data.options.length + 1);
    update({
      options,
      correctOptionIndex:
        data.correctOptionIndex >= options.length - 1
          ? options.length - 1
          : data.correctOptionIndex,
    });
  }, [data.options, data.correctOptionIndex, update]);

  const removeOption = useCallback(
    (index: number) => {
      if (data.options.length <= CREATE_QUESTION_MIN_OPTIONS) return;
      const options = data.options.filter((_, i) => i !== index);
      let newCorrect = data.correctOptionIndex;
      if (data.correctOptionIndex === index) {
        newCorrect = 0;
      } else if (data.correctOptionIndex > index) {
        newCorrect = data.correctOptionIndex - 1;
      }
      update({ options, correctOptionIndex: newCorrect });
    },
    [data.options, data.correctOptionIndex, update]
  );

  const setOptionText = useCallback(
    (index: number, value: QuestionOptionText) => {
      const options = [...data.options];
      options[index] = value;
      update({ options });
    },
    [data.options, update]
  );

  const sectionStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xl,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.normal,
    color: theme.palette.text.black,
    marginBottom: theme.spacing.sm,
  };

  const selectStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: `${theme.spacing.inputY} ${theme.spacing.inputX}`,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.normal,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.input.text,
    backgroundColor: theme.palette.input.background,
    border: hasError
      ? `1px solid ${theme.palette.error.main}`
      : `1px solid ${theme.palette.input.background}`,
    borderRadius: theme.shape.borderRadius.sm,
    boxSizing: 'border-box',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
  });

  const selectWrapperStyle: React.CSSProperties = {
    minWidth: 0,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const optionsSectionTitleStyle: React.CSSProperties = {
    ...labelStyle,
    marginBottom: theme.spacing.xs,
  };

  const optionsHintStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing.md,
  };

  const optionsHintRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.md,
    marginTop: theme.spacing.sm2,
  };

  const iconSizeForm = theme.palette.createCourse.iconSizeSmall;
  const lightBulbIconStyle: React.CSSProperties = {
    width: iconSizeForm,
    height: iconSizeForm,
    objectFit: 'contain',
    flexShrink: 0,
  };

  const optionBlockStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xl,
  };

  const optionBlockStyleLast: React.CSSProperties = {
    marginBottom: theme.spacing.lg,
  };

  const optionHeaderRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.sm,
  };

  const radioWrapStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.black,
  };

  const optionsTitleRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.xl,
    paddingTop: theme.spacing.lg,
    borderTop: `1px solid ${theme.palette.border.main}`,
  };

  const errorStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.error.main,
    marginTop: theme.spacing.xs,
  };

  const linkedCoursesBoxStyle: React.CSSProperties = {
    marginTop: theme.spacing.sm,
    padding: theme.spacing.md,
    border: `1px solid ${theme.palette.input.background}`,
    borderRadius: theme.shape.borderRadius.sm,
    backgroundColor: theme.palette.input.background,
    maxHeight: theme.palette.createCourse.linkedCoursesListMaxHeight,
    overflowY: 'auto' as const,
  };

  const linkedCourseCheckboxSize = theme.palette.createCourse.checkboxSize;
  const linkedCourseCheckboxWrapStyle: React.CSSProperties = {
    width: linkedCourseCheckboxSize,
    height: linkedCourseCheckboxSize,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius.xs,
    border: `1px solid ${theme.palette.border.input}`,
    backgroundColor: theme.palette.background.paper,
    outline: 'none',
  };
  const linkedCourseCheckboxWrapSelectedStyle: React.CSSProperties = {
    ...linkedCourseCheckboxWrapStyle,
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
  };

  const labelRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
  };

  const linkedCoursesTriggerStyle: React.CSSProperties = {
    width: '100%',
    minHeight: theme.palette.createCourse.iconBoxSize,
    padding: `${theme.spacing.inputY} ${theme.spacing.inputX}`,
    paddingRight: theme.palette.createCourse.selectDropdownPaddingRight,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    color:
      data.linkedCourseIds.length === 0
        ? theme.palette.text.secondary
        : theme.palette.text.primary,
    backgroundColor: theme.palette.input.background,
    border: `1px solid ${theme.palette.input.background}`,
    borderRadius: theme.shape.borderRadius.sm,
    boxSizing: 'border-box',
    outline: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  };

  const [linkedCoursesDropdownOpen, setLinkedCoursesDropdownOpen] =
    useState(false);
  const linkedCoursesDropdownRef = useRef<HTMLDivElement>(null);

  const optionsList = ensureOptionsLength(
    data.options,
    Math.max(data.options.length, CREATE_QUESTION_MIN_OPTIONS)
  );
  const canAddOption = optionsList.length < CREATE_QUESTION_MAX_OPTIONS;
  const canRemoveOption = optionsList.length > CREATE_QUESTION_MIN_OPTIONS;

  const linkedCourseTriggerLabel =
    data.linkedCourseIds.length === 0
      ? t('questionBank.create.select_courses_placeholder')
      : t('questionBank.create.courses_selected', {
          count: data.linkedCourseIds.length,
        });

  useEffect(() => {
    if (!linkedCoursesDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        linkedCoursesDropdownRef.current &&
        !linkedCoursesDropdownRef.current.contains(e.target as Node)
      ) {
        setLinkedCoursesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [linkedCoursesDropdownOpen]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <style>
        {`.CreateQuestionForm-linkedCoursesSection label:focus-within,
          .CreateQuestionForm-linkedCoursesSection input[type="checkbox"]:focus {
            outline: none;
          }
          .CreateQuestionForm-linkedCoursesSection label:focus-within,
          .CreateQuestionForm-linkedCoursesSection label:active {
            background: transparent;
          }
          .CreateQuestionForm-linkedCourseCheckbox {
            background: ${theme.palette.background.paper} !important;
            border: 1px solid ${theme.palette.border.input} !important;
          }
          .CreateQuestionForm-linkedCourseCheckbox.CreateQuestionForm-linkedCourseCheckbox--selected {
            background: ${theme.palette.primary.main} !important;
            border-color: ${theme.palette.primary.main} !important;
          }`}
      </style>
      {/* Question Text */}
      <MultilingualField
        label={t('questionBank.create.question_text')}
        required
        value={data.questionText}
        onChange={v => update({ questionText: v })}
        multiline
        placeholder={t('questionBank.create.question_text_placeholder')}
        validationError={
          errors.questionText
            ? t(`questionBank.create.${errors.questionText}`)
            : undefined
        }
      />

      {/* Difficulty Level + Category: duas colunas */}
      <div
        style={{
          ...sectionStyle,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: theme.spacing.md,
          alignItems: 'flex-start',
        }}
      >
        {/* Coluna Difficulty */}
        <div>
          <div
            style={{
              ...labelStyle,
              display: 'flex',
              marginBottom: theme.spacing.sm,
            }}
          >
            <span>{t('questionBank.create.difficulty_level')}</span>
          </div>
          <div style={selectWrapperStyle}>
            <select
              id="create-question-difficulty"
              value={data.difficultyLevel}
              onChange={e => update({ difficultyLevel: e.target.value })}
              style={selectStyle(Boolean(errors.difficultyLevel))}
              aria-required
              aria-invalid={Boolean(errors.difficultyLevel)}
            >
              <option value="">
                {t('questionBank.create.select_difficulty')}
              </option>
              {difficultyOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <img
              src={iconArrowDownStepCourses}
              alt=""
              width={theme.palette.createCourse.dragHandleSize}
              height={theme.palette.createCourse.dragHandleSize}
              style={{
                position: 'absolute',
                top: '50%',
                right: theme.spacing.sm,
                pointerEvents: 'none',
                objectFit: 'contain',
                transform: 'translateY(-50%)',
              }}
              aria-hidden
            />
          </div>
          {errors.difficultyLevel && (
            <p style={errorStyle} role="alert">
              {t(`questionBank.create.${errors.difficultyLevel}`)}
            </p>
          )}
        </div>

        {/* Coluna Category */}
        <div>
          <div
            style={{
              ...labelStyle,
              display: 'flex',
              marginBottom: theme.spacing.sm,
            }}
          >
            <span>{t('questionBank.create.category')}</span>
          </div>
          <div style={selectWrapperStyle}>
            <select
              id="create-question-category"
              value={data.categoryId}
              onChange={e => update({ categoryId: e.target.value })}
              style={selectStyle(Boolean(errors.categoryId))}
              aria-required
              aria-invalid={Boolean(errors.categoryId)}
            >
              <option value="">
                {t('questionBank.create.select_category')}
              </option>
              {categoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <img
              src={iconArrowDownStepCourses}
              alt=""
              width={theme.palette.createCourse.dragHandleSize}
              height={theme.palette.createCourse.dragHandleSize}
              style={{
                position: 'absolute',
                top: '50%',
                right: theme.spacing.sm,
                pointerEvents: 'none',
                objectFit: 'contain',
                transform: 'translateY(-50%)',
              }}
              aria-hidden
            />
          </div>
          {errors.categoryId && (
            <p style={errorStyle} role="alert">
              {t(`questionBank.create.${errors.categoryId}`)}
            </p>
          )}
        </div>
      </div>

      {/* Linked Course(s): dropdown + ícone no label */}
      <div
        className="CreateQuestionForm-linkedCoursesSection"
        style={sectionStyle}
        ref={linkedCoursesDropdownRef}
      >
        <div style={labelRowStyle}>
          <LinkIcon size={iconSizeForm} color={theme.palette.text.primary} />
          <span
            style={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.palette.text.primary,
            }}
          >
            {t('questionBank.create.linked_courses')}
          </span>
        </div>
        <div style={selectWrapperStyle}>
          <button
            type="button"
            style={linkedCoursesTriggerStyle}
            onClick={() => setLinkedCoursesDropdownOpen(prev => !prev)}
            aria-expanded={linkedCoursesDropdownOpen}
            aria-haspopup="listbox"
            aria-label={t('questionBank.create.linked_courses')}
          >
            {linkedCourseTriggerLabel}
          </button>
          <img
            src={iconArrowDownStepCourses}
            alt=""
            width={theme.palette.createCourse.dragHandleSize}
            height={theme.palette.createCourse.dragHandleSize}
            style={{
              position: 'absolute',
              top: '50%',
              right: theme.spacing.sm,
              pointerEvents: 'none',
              objectFit: 'contain',
              transform: 'translateY(-50%)',
            }}
            aria-hidden
          />
        </div>
        {linkedCoursesDropdownOpen && (
          <div
            style={linkedCoursesBoxStyle}
            role="listbox"
            aria-label={t('questionBank.create.linked_courses')}
          >
            {courseOptions.length === 0 ? (
              <span style={optionsHintStyle}>
                {t('questionBank.create.no_courses')}
              </span>
            ) : (
              courseOptions.map(opt => {
                const selected = data.linkedCourseIds.includes(opt.value);
                const toggleLinkedCourse = () => {
                  const next = selected
                    ? data.linkedCourseIds.filter(id => id !== opt.value)
                    : [...data.linkedCourseIds, opt.value];
                  update({ linkedCourseIds: next });
                };
                return (
                  <label
                    key={opt.value}
                    htmlFor={`linked-course-${opt.value}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                      padding: theme.spacing.xs,
                      cursor: 'pointer',
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.fontSize.base,
                      outline: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`linked-course-${opt.value}`}
                      checked={selected}
                      onChange={toggleLinkedCourse}
                      aria-label={opt.label}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: 0,
                        height: 0,
                        margin: 0,
                        padding: 0,
                        outline: 'none',
                        border: 'none',
                      }}
                    />
                    <div
                      className={`CreateQuestionForm-linkedCourseCheckbox${selected ? ' CreateQuestionForm-linkedCourseCheckbox--selected' : ''}`}
                      style={
                        selected
                          ? linkedCourseCheckboxWrapSelectedStyle
                          : linkedCourseCheckboxWrapStyle
                      }
                      aria-hidden
                    >
                      {selected && (
                        <img
                          src={iconCheckSmallStep3Courses}
                          alt=""
                          width={linkedCourseCheckboxSize - 4}
                          height={linkedCourseCheckboxSize - 4}
                        />
                      )}
                    </div>
                    {opt.label}
                  </label>
                );
              })
            )}
          </div>
        )}
        <p
          style={{
            ...optionsHintStyle,
            marginTop: theme.spacing.sm,
            marginBottom: 0,
          }}
        >
          {t('questionBank.create.linked_courses_hint')}
        </p>
      </div>

      {/* Answer Options */}
      <div style={sectionStyle}>
        <div style={optionsTitleRowStyle}>
          <h3 style={{ ...optionsSectionTitleStyle, marginBottom: 0 }}>
            {t('questionBank.create.answer_options_title')}
          </h3>
          {canAddOption && (
            <Button
              type="button"
              onClick={addOption}
              size="small"
              style={{
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                fontSize: theme.typography.fontSize.sm,
                fontFamily: theme.typography.fontFamily,
                border: 'none',
                borderRadius: theme.shape.borderRadius.sm,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                cursor: canAddOption ? 'pointer' : 'not-allowed',
                opacity: canAddOption ? 1 : 0.6,
              }}
            >
              <span style={{ marginRight: theme.spacing.xs }}>+</span>
              {t('questionBank.create.add_option')}
            </Button>
          )}
        </div>
        <div style={optionsHintRowStyle}>
          <img
            src={iconLightBulbStepCourses}
            alt=""
            style={lightBulbIconStyle}
            aria-hidden
          />
          <span style={{ ...optionsHintStyle, marginBottom: 0 }}>
            {t('questionBank.create.answer_options_hint')}
          </span>
        </div>
        {errors.options && (
          <p style={errorStyle} role="alert">
            {t(`questionBank.create.${errors.options}`)}
          </p>
        )}
        {optionsList.map((opt, idx) => {
          const isLast = idx === optionsList.length - 1;
          return (
            <div
              key={idx}
              style={isLast ? optionBlockStyleLast : optionBlockStyle}
            >
              <div style={optionHeaderRowStyle}>
                <label style={radioWrapStyle} htmlFor={`correct-option-${idx}`}>
                  <input
                    id={`correct-option-${idx}`}
                    type="radio"
                    name="correctOption"
                    checked={data.correctOptionIndex === idx}
                    onChange={() => update({ correctOptionIndex: idx })}
                    aria-label={t('questionBank.create.mark_correct_aria', {
                      index: idx + 1,
                    })}
                    style={{ accentColor: theme.palette.text.black }}
                  />
                  <span>{t('questionBank.create.mark_correct')}</span>
                </label>
                {canRemoveOption && (
                  <button
                    type="button"
                    onClick={() => removeOption(idx)}
                    aria-label={t('questionBank.create.remove_option_aria', {
                      index: idx + 1,
                    })}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: theme.spacing.xs,
                      cursor: 'pointer',
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.fontSize.xl,
                      lineHeight: theme.typography.lineHeight.tight,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              <MultilingualField
                label={`${t('questionBank.create.option_label')} ${idx + 1}`}
                required
                value={opt}
                onChange={v => setOptionText(idx, v)}
                placeholder={t('questionBank.create.option_placeholder', {
                  index: idx + 1,
                })}
                validationError={
                  errors.optionText?.[idx]
                    ? t(`questionBank.create.${errors.optionText[idx]}`)
                    : undefined
                }
                id={`option-${idx}`}
              />
            </div>
          );
        })}
      </div>

      {/* Explanation */}
      <MultilingualField
        label={t('questionBank.create.explanation')}
        value={data.explanation}
        onChange={v => update({ explanation: v })}
        multiline
        placeholder={t('questionBank.create.explanation_placeholder')}
      />

      {/* Footer */}
      <div style={footerStyle}>
        <Button
          type="button"
          onClick={onCancel}
          backgroundColor={theme.palette.background.paper}
          color={theme.palette.text.primary}
          style={{ border: `1px solid ${theme.palette.border.main}` }}
        >
          {t('questionBank.create.cancel')}
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit}
          backgroundColor={theme.palette.primary.main}
          color={theme.palette.primary.contrastText}
          disabled={saving}
        >
          <img
            src={iconCheckWhiteModalCourses}
            alt=""
            width={iconSizeForm}
            height={iconSizeForm}
            style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }}
            aria-hidden
          />
          {t('questionBank.create.save_question')}
        </Button>
      </div>
    </form>
  );
}
